import { FormField, Heading, RadioChipGroup, RadioChipOptions, Text } from '@expressable/syntax-ds';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import { EvaluationTimeSlot } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { UseEvaluationCalendarResult } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { EvaluationScheduleFormType } from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { FC, useEffect, useMemo, useState } from 'react';
import { useController, UseControllerReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DAY_MONTH_YEAR, WEEK_DAY_LONG } from 'utils/date';
import { SignupError } from 'utils/errors/debuggableError';

type Props = {
  onChange?: (timeSlot?: EvaluationTimeSlot) => void;
  name: string;
  calendarData: Extract<UseEvaluationCalendarResult, { isLoading: false }>;
  firstSessionTherapistId: string;
};

// Only meant to be used for picking second recurring time slot on signup, do not reuse directly (copy the parts you need)

export const SecondSplitTimeSlotPicker: FC<Props> = ({ onChange, name, calendarData, firstSessionTherapistId }) => {
  // @TODO(juan): types need to be cleaned up here, and properly separate the component from RHF schema
  const {
    field,
    fieldState: { error },
  } = useController({ name: name as 'timeSlot' }) as UseControllerReturn<
    Partial<EvaluationScheduleFormType>,
    'timeSlot'
  >;

  const { currentAvailability, dayIndex, onChangeDay } = calendarData;

  const { availabilityByTherapist, day: currentDay } = currentAvailability;

  const therapistAvailabilitySlots =
    availabilityByTherapist.find(a => a.therapist.therapistID === firstSessionTherapistId)?.timeSlots || [];

  // Define if there are no available slots (depends on the timelineSlots and if all requests are resolved)
  const noAvailableSlots = therapistAvailabilitySlots.length === 0;

  const { format: formatInTimezone } = useDisplayTimezone();

  const dayOptions: RadioChipOptions = useMemo(() => {
    return calendarData.allTimeSlots.map(({ day, availabilityByTherapist }, index) => {
      const slots = availabilityByTherapist.find(a => a.therapist.therapistID === firstSessionTherapistId);
      const hasSlots = slots ? slots.timeSlots.length > 0 : false;
      return {
        value: `${index}`,
        label: formatInTimezone(day, `EEEE`),
        disabled: !hasSlots,
      };
    });
  }, [calendarData]);

  const onChangeTimeSlot = (slot?: EvaluationTimeSlot) => {
    onChange?.(slot);
    field.onChange(slot);
  };

  const { t } = useTranslation();

  const GhostAnchor = () => <div ref={field.ref} className="hidden" />;

  // Start day carousel on first day with available slots
  const [startIndex, setStartIndex] = useState<number | undefined>();

  useEffect(() => {
    const dayOptionIndex = dayOptions.findIndex(option => !option.disabled);

    if (dayOptionIndex !== -1 && dayIndex < dayOptionIndex) {
      onChangeDay(dayOptionIndex);
      setStartIndex(dayOptionIndex);
    }
  }, [dayOptions, dayIndex, onChangeDay]);

  return (
    <>
      <FormField
        name={noAvailableSlots ? name : undefined}
        label={t('directSignup.schedule.split.second.dayPicker.label')}
        config={{ order: { error: 'top', hint: 'top' } }}
      >
        <RadioChipGroup
          htmlName="daypicker"
          chips={dayOptions}
          variant="standard"
          value={`${dayIndex}`}
          startIndex={startIndex}
          onChange={e => {
            onChangeTimeSlot(undefined);
            onChangeDay(parseInt(e.target.value, 10));
          }}
          standalone
        />
      </FormField>

      {noAvailableSlots && (
        <Text className="text-center" variant="h6">
          {t('directSignup.schedule.noSlots')} <GhostAnchor />
        </Text>
      )}
      <Heading level="h4" noDefaultMargins>
        {t('directSignup.schedule.split.second.currentDayTitle', {
          day: format(currentDay, WEEK_DAY_LONG),
          date: format(currentDay, DAY_MONTH_YEAR),
        })}
      </Heading>

      {therapistAvailabilitySlots.length > 0 && (
        <FormField
          name={name}
          label={t('directSignup.schedule.split.second.timePicker.label')}
          config={{
            order: { error: 'bottom', hint: 'top' },
            labelSpacing: 'lg',
          }}
          // Note(juan): placeholder manual styles and build while we experiment with this component, will later be ported to syntax
        >
          <RadioChipGroup
            htmlName={`timeslotPicker}`}
            chips={therapistAvailabilitySlots.map(slot => ({
              value: slot.id,
              label: formatInTimezone(slot.time, 'h:mm a'),
            }))}
            variant="standard"
            onChange={e => {
              const pickedId = e.target.value;
              const slot = therapistAvailabilitySlots.find(s => s.id === pickedId);
              if (!slot) {
                captureException(new SignupError(`Unable to find slot with id: ${pickedId}`, {
                  debugData: {
                    pickedId,
                    therapistAvailability: JSON.stringify(therapistAvailabilitySlots),
                  },
                }));
              }

              onChangeTimeSlot(slot);
            }}
            value={field.value?.id ?? ''}
            standalone
            error={error}
            className="[&>*]:mobile-xs-d:grid-cols-2" //@TODO will be moved to syntax
          />
        </FormField>
      )}

      <GhostAnchor />
    </>
  );
};
